import { assert } from '@ember/debug';
import type { Column } from 'slickgrid';
import type { ColumnFilter, FieldValues } from '../components/aditools-device-table.ts';
import type { DeviceTableDeviceData } from '@ebiz/aditools-databook';

const messages = {
  select_blanks_filter: '(Blanks)',
  select_all_filter: 'Show All',
  checkbox_filter_selected: 'values selected',
  value: 'value',
};

export default class MultivalueCheckboxFilter implements ColumnFilter<DeviceTableDeviceData> {
  filterValues: FieldValues[];
  value: string;
  qaName?: string;

  constructor(filterValues: FieldValues[], initialValue?: string, qaName?: string) {
    this.filterValues = filterValues;
    this.value = initialValue ? initialValue : '';
    this.qaName = qaName;
  }

  get initFilterCount(): string {
    if (this.value === '|') {
      // all were unchecked
      return '0';
    } else {
      // combination of 'Show ALl' and other values
      const values = this.value.split('|');
      if (this.value === '') {
        // value is 'Show All'
        return this.filterValues.length.toString();
      } else {
        return values.length.toString();
      }
    }
  }

  /*  INTERFACE METHODS
      We are setting "this.value" in reset, unpack, update.
      This property is what we are tracking in filter, setting checkbox checked, and displaying initial filter count
      If 'Show All' is checked, automatically assign '' in this.value
      If all items were checked/packed except 'Show All', automatically assign '' in this.value
      Expected values of this.value:
        a. '|' : all were unchecked
        b. ''   : all were checked
        c. "1|2|3|4" : sample combination/rest of the values in checkbox
  */
  populate(column: Column<DeviceTableDeviceData>): HTMLElement {
    if (!column) {
      throw 'column passed to populate is invalid';
    }
    const selection = this.generateDOMElement(column);
    assert('At least one element is always generated', selection);

    return selection;
  }

  update(element: HTMLInputElement): void {
    const columnId = element.id.split('_').slice(-1)[0] as string;
    if (element.id.includes('cbSelectAll')) {
      if (element.checked) {
        this.value = '';
      } else {
        this.value = '|';
      }
    } else {
      this.setCheckboxFilterData(columnId);
    }

    this.handleCheckboxChanged(columnId);
  }

  /* Should filter base on "this.value" */
  filter(field: string): boolean {
    if (this.value === '|') {
      // all were unchecked
      return false;
    }

    // could be combination of other values in checkbox
    if (this.value !== '') {
      if (this.value === '-') {
        // only blank is selected
        return field === '' || field === '-' || field === null || field === undefined;
      } else {
        const values = this.value.split('|').map((item) => {
          return item.trim();
        });
        // selected could be combination of blank and other values
        if (field) {
          const fieldList = field.split(',').map((item) => {
            return item.trim();
          });
          // match fieldList with the provided values
          return values.some((element) => {
            return fieldList.includes(element);
          });
        } else {
          // field is blank
          return values.includes('-');
        }
      }
    }

    // default-'Show All' is checked
    return true;
  }

  /* Called if there are packedFilters from aditools-device-table */
  unpack(packedValues: string): void {
    if (packedValues) {
      const initialValues = JSON.parse(packedValues);
      if (Object.prototype.hasOwnProperty.call(initialValues, 'value')) {
        this.value = initialValues.value;
      }
    }
  }

  pack(): string {
    const packedValues = JSON.stringify({ value: this.value });
    return packedValues;
  }

  reset(): void {
    this.value = '';
  }

  /* Guard to proceed with unpack/reset in aditools-device-table: should be false */
  hasValue(): boolean {
    return this.value !== '';
  }

  /* FUNCTIONS */
  generateDOMElement(column: Column<DeviceTableDeviceData>): HTMLDivElement {
    const filterCountMessage = this.initFilterCount + ' ' + messages.checkbox_filter_selected;
    this.generateCheckboxContainer(column);

    const divElement = document.createElement('div');
    divElement.id = `checkbox-filter-container${column.id}`;
    divElement.innerHTML = `<div id="checkbox-filter-toggle${column.id}" class="checkbox-filter-toggle">
        <span id="checkbox-toggle-text${column.id}" class="checkbox-toggle-text" title="${filterCountMessage}">${filterCountMessage}</span>
        <span class="drop-down-icon"></span>
       </div>`;
    return divElement;
  }

  generateCheckboxContainer(column: Column<DeviceTableDeviceData>): void {
    const checkboxContainerItems = this.generateCheckboxContainerItems(column);
    const checkboxContainerElement = document.createElement('div');
    checkboxContainerElement.id = `checkbox-container${column.id}`;
    checkboxContainerElement.className = 'checkbox-container';
    checkboxContainerElement.setAttribute('data-column-id', column.id.toString());
    if (this.qaName) {
      checkboxContainerElement.setAttribute('data-qa-element', `device_table_${this.qaName}_checkbox`);
    }
    checkboxContainerElement.innerHTML = checkboxContainerItems;
    // insert checkbox container into device-table-overlays
    const overlayContainer = document.getElementById('device-table-overlays');
    if (overlayContainer) {
      const checkboxContainer = document.getElementById(`checkbox-container${column.id}`);
      if (checkboxContainer === null) {
        // if checkboxContainer is not existing, we can safely append it to overlayContainer
        overlayContainer.appendChild(checkboxContainerElement);
      }
    }
  }

  generateCheckboxContainerItems(column: Column<DeviceTableDeviceData>): string {
    const checkboxHtml = [],
      divider = `<div class='checkbox-divider'></div>`,
      selectAllIdName = `cbSelectAll_${column.id}`;

    // Build the Show All checkbox to the list.
    const selectAll = this.buildCheckboxString(
      selectAllIdName,
      selectAllIdName,
      '',
      messages.select_all_filter,
      '',
      this.setCheckboxItemChecked(''),
      false,
    );
    checkboxHtml.push(selectAll + divider);

    for (const [index, val] of this.filterValues.entries()) {
      const className = `cbfilter_${column.id}`,
        idName = `cbfilter_${index}_${column.id}`;
      if (val.value === '-') {
        // Build the (Blanks) checkbox to the list.
        const blankCheckbox = this.buildCheckboxString(
          idName,
          idName,
          val.value,
          val.display ?? '',
          className,
          this.setCheckboxItemChecked(val.value),
          false,
        );
        checkboxHtml.push(blankCheckbox + divider);
      } else {
        // Build the remaining checkbox items to the list.
        const checkboxItem = this.buildCheckboxString(
          idName,
          idName,
          val.value,
          val.display ?? '',
          className,
          this.setCheckboxItemChecked(val.value),
          false,
        );
        checkboxHtml.push(checkboxItem);
      }
    }

    return checkboxHtml.join('');
  }

  /* Track this.value to set checbox if checked or not */
  setCheckboxItemChecked(value: string): boolean {
    if (this.value === '|') {
      // all were unchecked
      return false;
    }

    // could be combination of other values in checkbox
    const values = this.value.split('|');
    if (this.value !== '') {
      return values.includes(value);
    }

    // default-'Show All' is checked
    return true;
  }

  buildCheckboxString(
    id: string,
    name: string,
    value: string,
    display: string,
    cbClass?: string,
    checked?: boolean,
    disabled?: boolean,
  ) {
    return `<div class='menu-item ${disabled ? 'disabled' : ''}'>
    <input id='${id}' name='${name}' type='checkbox' value='${value}'
      class='checkbox-filter ${cbClass ? cbClass : ''} ${disabled ? 'disabled' : ''}'
      ${checked ? 'checked' : ''}
      ${disabled ? 'disabled' : ''}
      />
    <label class='checkbox-text ${disabled ? 'disabled' : ''}' title='${display}'>${display}</label></div>`;
  }

  updateFilterCount(columnId?: string) {
    let checkedCount = 0;
    const cbFilters = document.querySelectorAll('.cbfilter_' + columnId);
    if (cbFilters.length > 0) {
      cbFilters.forEach((cb) => {
        const checkbox = cb as HTMLInputElement;
        if (checkbox.type.toLowerCase() === 'checkbox' && checkbox.checked) {
          checkedCount++;
        }
      });
    }

    const text = checkedCount + ' ' + messages.checkbox_filter_selected;
    const checkboxToggleText = document.querySelector(`#checkbox-toggle-text${columnId}`);
    if (checkboxToggleText) {
      checkboxToggleText.setAttribute('title', text);
      checkboxToggleText.innerHTML = text;
    }
  }

  updateSelectAllChecked(columnId: string) {
    const selectAll = document.getElementById('cbSelectAll_' + columnId) as HTMLInputElement;
    const cbFilters = document.querySelectorAll('.cbfilter_' + columnId);
    const cbFiltersChecked = document.querySelectorAll(`.cbfilter_${columnId}:checked`);
    if (cbFiltersChecked.length === cbFilters.length) {
      selectAll.checked = true;
    } else {
      selectAll.checked = false;
    }

    this.updateFilterCount(columnId);
  }

  updateCheckboxChecked(columnId: string) {
    const selectAll = document.getElementById('cbSelectAll_' + columnId) as HTMLInputElement;
    const cbFilters = document.querySelectorAll('.cbfilter_' + columnId);
    if (cbFilters.length > 0) {
      cbFilters.forEach((cb) => {
        (cb as HTMLInputElement).checked = selectAll.checked;
      });

      this.updateFilterCount(columnId);
    }
  }

  handleCheckboxChanged(columnId: string) {
    const selectAllChanged = () => {
      this.updateCheckboxChecked(columnId);
    };

    const checkboxChanged = () => {
      this.updateSelectAllChecked(columnId);
    };

    document.getElementById('cbSelectAll_' + columnId)?.addEventListener('change', selectAllChanged);
    document.querySelectorAll('.cbfilter_' + columnId)?.forEach((cb) => {
      cb.addEventListener('change', checkboxChanged);
    });
  }

  /* Part of 'update' interface method. For 'Blank' and other values in checkbox items */
  setCheckboxFilterData(columnId: string) {
    const cbFilters = document.querySelectorAll('.cbfilter_' + columnId);
    const values: string[] = [];

    if (cbFilters.length > 0) {
      cbFilters.forEach((cb) => {
        const checkbox = cb as HTMLInputElement;
        if (checkbox.type.toLowerCase() === 'checkbox' && checkbox.checked) {
          // extract values that are checked
          values.push(checkbox.value);
        }
      });
    }

    if (values.length === 0) {
      // all checkboxes unchecked
      this.value = '|';
    } else {
      if (values.length === this.filterValues.length) {
        // all were checked except Show All, automatically assign '' in this.value
        this.value = '';
      } else {
        this.value = values.join('|');
      }
    }
  }
}
